import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { usePinSecurity } from "@/contexts/PinContext";
import { formatDateES } from "@/lib/utils-app";
import { Copy, Loader2, Lock, Pencil, History, Check, X, MessageSquare, GraduationCap } from "lucide-react";
import ExamHistoryPanel from "./ExamHistoryPanel";

const PENDING_FEES_LABELS = { si: "Sí", no: "No", no_lo_se: "No lo sabe" };

// Misma estructura de campos que el módulo Cuestionarios (QuestionnaireDetail).
const FIELD_DEFS = [
  { key: "transferStudent", label: "Trasladado", type: "bool" },
  { key: "previousDrivingSchool", label: "Autoescuela origen", type: "text" },
  { key: "theoryApproved", label: "Teórico aprobado", type: "bool" },
  { key: "theoryAttempts", label: "Intentos teórico", type: "int" },
  { key: "approvedTheoryAttempt", label: "Aprobado en intento", type: "int" },
  {
    key: "lastTheoryResult", label: "Último resultado", type: "select",
    options: [
      { value: "", label: "—" },
      { value: "Apto", label: "Apto" },
      { value: "No apto", label: "No apto" },
      { value: "No me he presentado", label: "No me he presentado" },
    ],
  },
  {
    key: "pendingTheoryFees", label: "Tasas teórico pendientes", type: "select",
    options: [
      { value: "", label: "—" },
      { value: "si", label: "Sí" },
      { value: "no", label: "No" },
      { value: "no_lo_se", label: "No lo sabe" },
    ],
  },
  { key: "practicalAttemptsOtherSchool", label: "Prácticos otra autoescuela", type: "int" },
  { key: "practicalAttemptsNatur", label: "Prácticos Natur", type: "int" },
  { key: "feesPaid", label: "Tasas pagadas", type: "int" },
];

// Campos de expediente DGT (colección students, gestionados por exam_workflow).
const EXPEDIENTE_DEFS = [
  { key: "suspensos_count", label: "Suspensos acumulados", type: "int" },
  { key: "next_exam_allowed_from", label: "Próximo examen permitido desde", type: "date" },
  { key: "apto_date", label: "Fecha de APTO", type: "date" },
];

const FIELD_LABELS = Object.fromEntries(
  [...FIELD_DEFS, ...EXPEDIENTE_DEFS].map((f) => [f.key, f.label])
);
FIELD_LABELS.notes = "Observaciones";

function displayValue(def, value) {
  if (def.type === "bool") return value ? "Sí" : "No";
  if (def.key === "pendingTheoryFees") return PENDING_FEES_LABELS[value] || "—";
  if (def.key === "approvedTheoryAttempt" && !value) return "—";
  if (def.type === "date") return value ? formatDateES(value) : "—";
  if (value === "" || value === null || value === undefined) return "—";
  return String(value);
}

function auditValue(field, value) {
  if (typeof value === "boolean") return value ? "Sí" : "No";
  if (field === "pendingTheoryFees") return PENDING_FEES_LABELS[value] || String(value ?? "—");
  if (value === "" || value === null || value === undefined) return "—";
  return String(value);
}

function formatAuditDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${formatDateES(iso.slice(0, 10))} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function FieldRow({ def, value, editMode, editControl }) {
  return (
    <div className="flex justify-between items-center gap-2 py-1 border-b border-neutral-100 last:border-0">
      <span className="text-neutral-500 shrink-0">{def.label}</span>
      {editMode ? (
        <div className="w-44">{editControl}</div>
      ) : (
        <span className="font-medium text-neutral-800 text-right" data-testid={`logistics-value-${def.key}`}>
          {displayValue(def, value)}
        </span>
      )}
    </div>
  );
}

export default function ExamRequestLogisticsDialog({ request, open, onClose }) {
  const pin = usePinSecurity();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [allTasas, setAllTasas] = useState([]);

  const load = useCallback(async () => {
    if (!request?.id) return;
    setLoading(true);
    try {
      const sid0 = request?.student_id;
      if (sid0) {
        // Primero las tasas: `with_state` sincroniza el estado activa/finalizada.
        const { data: ts } = await api.get(`/students/${sid0}/exam-requests`);
        setAllTasas(ts || []);
      }
      const { data: d } = await api.get(`/exam-requests/${request.id}/logistics`);
      setData(d);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [request?.id]);

  useEffect(() => {
    if (open) {
      setEditMode(false);
      load();
    } else {
      setData(null);
    }
  }, [open, load]);

  const req = data?.request;
  // Registro logístico completo: cuestionario vinculado o valores por defecto.
  const q = data?.questionnaire || data?.defaults || {};
  const exp = data?.expediente || {};

  const copySummary = () => {
    navigator.clipboard.writeText(data?.summary || "");
    toast.success("Resumen copiado al portapapeles");
  };

  const startEdit = async () => {
    try {
      await pin.require("exam_request.logistics.edit");
    } catch {
      return;
    }
    const init = {};
    FIELD_DEFS.forEach((f) => {
      init[f.key] = q?.[f.key] ?? (f.type === "int" ? 0 : f.type === "bool" ? false : "");
    });
    init.notes = q?.notes ?? "";
    EXPEDIENTE_DEFS.forEach((f) => {
      init[f.key] = exp?.[f.key] ?? (f.type === "int" ? 0 : "");
    });
    setDraft(init);
    setEditMode(true);
  };

  const save = async () => {
    const changed = {};
    FIELD_DEFS.forEach((f) => {
      let v = draft[f.key];
      if (f.type === "int") v = Math.max(0, parseInt(v, 10) || 0);
      if ((q?.[f.key] ?? "") !== v && !(f.type === "bool" && !!q?.[f.key] === !!v)) changed[f.key] = v;
    });
    if ((q?.notes ?? "") !== (draft.notes ?? "")) changed.notes = draft.notes ?? "";
    EXPEDIENTE_DEFS.forEach((f) => {
      let v = draft[f.key];
      if (f.type === "int") v = Math.max(0, parseInt(v, 10) || 0);
      if ((exp?.[f.key] ?? (f.type === "int" ? 0 : "")) !== v) changed[f.key] = v;
    });
    if (Object.keys(changed).length === 0) {
      toast.info("No hay cambios que guardar");
      return;
    }
    setSaving(true);
    try {
      const { data: d } = await api.patch(`/exam-requests/${request.id}/logistics`, changed);
      setData(d);
      setEditMode(false);
      toast.success("Datos logísticos actualizados");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const editField = (def) => {
    const v = draft[def.key];
    const base =
      "w-full rounded-lg border border-neutral-300 bg-white px-2 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500";
    if (def.type === "bool") {
      return (
        <select
          data-testid={`logistics-edit-${def.key}`}
          value={v ? "si" : "no"}
          onChange={(e) => setDraft({ ...draft, [def.key]: e.target.value === "si" })}
          className={base}
        >
          <option value="si">Sí</option>
          <option value="no">No</option>
        </select>
      );
    }
    if (def.type === "select") {
      return (
        <select
          data-testid={`logistics-edit-${def.key}`}
          value={v ?? ""}
          onChange={(e) => setDraft({ ...draft, [def.key]: e.target.value })}
          className={base}
        >
          {def.options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      );
    }
    if (def.type === "int") {
      return (
        <input
          data-testid={`logistics-edit-${def.key}`}
          type="number"
          min="0"
          value={v ?? 0}
          onChange={(e) => setDraft({ ...draft, [def.key]: e.target.value })}
          className={base}
        />
      );
    }
    if (def.type === "date") {
      return (
        <input
          data-testid={`logistics-edit-${def.key}`}
          type="date"
          value={v ?? ""}
          onChange={(e) => setDraft({ ...draft, [def.key]: e.target.value })}
          className={base}
        />
      );
    }
    return (
      <input
        data-testid={`logistics-edit-${def.key}`}
        type="text"
        value={v ?? ""}
        onChange={(e) => setDraft({ ...draft, [def.key]: e.target.value })}
        className={base}
      />
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="exam-logistics-dialog">
        <DialogHeader>
          <DialogTitle>
            Solicitud · Tasas <span className="font-mono">{request?.tasas_number}</span>
          </DialogTitle>
          <DialogDescription>
            Datos logísticos reales que NEO utiliza para gestionar esta solicitud.
          </DialogDescription>
        </DialogHeader>

        {loading || !data ? (
          <div className="text-sm text-neutral-500 py-6 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        ) : (
          <div className="space-y-5">
            {/* Datos de la solicitud */}
            <section>
              <h4 className="text-sm font-bold uppercase tracking-wider text-neutral-500 mb-2">Datos de la solicitud</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm" data-testid="logistics-request-fields">
                {[
                  ["Nº de tasas", req?.tasas_number || "—"],
                  ["Fecha de presentación", req?.presented_date ? formatDateES(req.presented_date) : "—"],
                  ["Estado", req?.status === "active" ? "Activa" : req?.status === "refunded" ? "Reembolsada" : "Finalizada"],
                  ["Intentos usados", `${req?.attempts_used ?? 0}/2`],
                  ["Saldo de la tasa", allTasas.find((t) => t.id === req?.id)?.state?.label || "—"],
                  ["Documentación completa", req?.documentation_complete ? "Sí" : "No"],
                  ["Tasas pagadas", req?.fees_paid ? "Sí" : "No"],
                  ["Presentada en tráfico", req?.presented_traffic ? "Sí" : "No"],
                  ["Observaciones", req?.notes || "—"],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2 py-1 border-b border-neutral-100 last:border-0">
                    <span className="text-neutral-500">{k}</span>
                    <span className="font-medium text-neutral-800 text-right">{v}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* V1.79 · Historial de exámenes / Tasas */}
            <section>
              <h4 className="text-sm font-bold uppercase tracking-wider text-neutral-500 mb-2">
                Historial de exámenes
              </h4>
              <ExamHistoryPanel
                studentId={req?.student_id}
                tasas={allTasas}
                onChanged={() => load()}
              />
            </section>

            <div className="border-t border-neutral-100" />

            {/* Resumen automático — mismo formato que Cuestionarios */}
            <section>
              <div className="flex items-center justify-between gap-2 mb-2">
                <h4 className="text-sm font-bold uppercase tracking-wider text-neutral-500">Resumen automático</h4>
                <button type="button" onClick={copySummary} className="btn-ghost !h-8 !px-2 !text-xs border border-neutral-200" data-testid="logistics-copy-summary">
                  <Copy className="h-3 w-3" /> Copiar
                </button>
              </div>
              <pre className="rounded-xl bg-neutral-50 border border-neutral-200 px-4 py-3 text-sm text-neutral-800 whitespace-pre-wrap font-sans" data-testid="logistics-summary">
{data.summary}
              </pre>
              {!data.questionnaire && (
                <p className="text-[11px] text-neutral-400 mt-1.5" data-testid="logistics-no-questionnaire">
                  Este alumno aún no tiene cuestionario vinculado — se muestran los valores por defecto.
                  Al guardar la primera corrección se creará su registro logístico.
                </p>
              )}
            </section>

            {/* Todos los campos */}
            <section>
              <div className="flex items-center justify-between gap-2 mb-2">
                <h4 className="text-sm font-bold uppercase tracking-wider text-neutral-500">Todos los campos</h4>
                {!editMode ? (
                  <button type="button" onClick={startEdit} className="btn-ghost !h-8 !px-2 !text-xs border border-neutral-200" data-testid="logistics-edit-btn">
                    <Lock className="h-3 w-3" /> Editar (PIN)
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setEditMode(false)} disabled={saving} className="btn-ghost !h-8 !px-2 !text-xs border border-neutral-200" data-testid="logistics-cancel-btn">
                      <X className="h-3 w-3" /> Cancelar
                    </button>
                    <button type="button" onClick={save} disabled={saving} className="btn-primary !h-8 !px-3 !text-xs" data-testid="logistics-save-btn">
                      {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Guardar
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm" data-testid="logistics-fields">
                {FIELD_DEFS.map((def) => (
                  <FieldRow key={def.key} def={def} value={q[def.key]} editMode={editMode} editControl={editField(def)} />
                ))}
              </div>
              {editMode ? (
                <div className="mt-3">
                  <label className="text-xs font-semibold text-neutral-500 mb-1 block">Observaciones del alumno</label>
                  <textarea
                    data-testid="logistics-edit-notes"
                    value={draft.notes ?? ""}
                    onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                    rows={3}
                    className="w-full rounded-lg border border-neutral-300 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              ) : q.notes ? (
                <div className="mt-3 rounded-xl bg-neutral-50 border border-neutral-200 p-3 text-sm">
                  <div className="text-xs font-semibold text-neutral-500 mb-1 flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" /> Observaciones del alumno
                  </div>
                  <div className="whitespace-pre-wrap">{q.notes}</div>
                </div>
              ) : null}
            </section>

            {/* Expediente DGT */}
            <section>
              <h4 className="text-sm font-bold uppercase tracking-wider text-neutral-500 mb-2 flex items-center gap-1.5">
                <GraduationCap className="h-3.5 w-3.5" /> Expediente DGT
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm" data-testid="logistics-expediente-fields">
                {EXPEDIENTE_DEFS.map((def) => (
                  <FieldRow key={def.key} def={def} value={exp[def.key]} editMode={editMode} editControl={editField(def)} />
                ))}
                <div className="flex justify-between items-center gap-2 py-1 border-b border-neutral-100 last:border-0">
                  <span className="text-neutral-500 shrink-0">Expediente cerrado</span>
                  <span className="font-medium text-neutral-800 text-right" data-testid="logistics-value-expediente_closed_at">
                    {exp.expediente_closed_at ? formatAuditDate(exp.expediente_closed_at) : "—"}
                  </span>
                </div>
              </div>
            </section>

            {/* Auditoría */}
            <section>
              <h4 className="text-sm font-bold uppercase tracking-wider text-neutral-500 mb-2 flex items-center gap-1.5">
                <History className="h-3.5 w-3.5" /> Historial de modificaciones
              </h4>
              {(data.audit || []).length === 0 ? (
                <p className="text-xs text-neutral-400" data-testid="logistics-audit-empty">Sin modificaciones registradas.</p>
              ) : (
                <div className="space-y-1.5" data-testid="logistics-audit-list">
                  {data.audit.map((a) => (
                    <div key={a.id} className="rounded-lg border border-neutral-200 bg-neutral-50/60 px-3 py-2 text-xs flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <Pencil className="h-3 w-3 text-neutral-400 shrink-0" />
                      <span className="font-semibold text-neutral-700">{FIELD_LABELS[a.field] || a.field}:</span>
                      <span className="text-neutral-500 line-through">{auditValue(a.field, a.old_value)}</span>
                      <span className="text-neutral-400">→</span>
                      <span className="font-semibold text-emerald-700">{auditValue(a.field, a.new_value)}</span>
                      <span className="ml-auto text-neutral-400">{a.actor} · {formatAuditDate(a.at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
